import { ChangeEvent, useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import authService from '../../services/authService';
import isCpfValid from '../../utils/isCpfValid';
import useErrors from '../../hooks/useErrors';

import formatCpf from '../../utils/formatCpf';
import hasSequentialNumbers from '../../utils/hasSequentialNumbers';
import useApiCall from '../../hooks/useApiCall';

export default function useForgotPassword() {
  const [isLoading, setIsLoading] = useState(false);
  const [cpf, setCpf] = useState('');
  const [visualizationKind, setVisualizationKind] = useState<'sendCode' | 'changePassword'>('sendCode');
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [validationCode, setValidationCode] = useState('');

  const userCode = useRef('');
  const userProfile = useRef('');

  const navigate = useNavigate();
  const {
    setError, removeError, getErrorMessageByFieldName, errors,
  } = useErrors();
  const { apiCall } = useApiCall();

  const isReadyToSendCode = (cpf && errors.length === 0);

  const isReadyToResetPassword = (validationCode && errors.length === 0 && password);

  function handleCpfChange(event: ChangeEvent<HTMLInputElement>) {
    setCpf(formatCpf(event.target.value));

    if (!isCpfValid(event.target.value)) {
      setError({ field: 'cpf', message: 'Cpf inválido!' });
    } else {
      removeError('cpf');
    }
  }

  function handlePasswordChange(event: ChangeEvent<HTMLInputElement>) {
    setPassword(event.target.value);

    if (event.target.value.length < 8 || hasSequentialNumbers(event.target.value)) {
      setError({ field: 'password', message: 'Senha deve conter ter 8 caracteres, e não pode haver números sequenciais!' });
    } else if (event.target.value) {
      removeError('password');
    } if (event.target.value !== passwordConfirmation) {
      setError({ field: 'passwordConfirmation', message: 'Senhas divergentes' });
    } else if (event.target.value === passwordConfirmation) {
      removeError('passwordConfirmation');
    }
  }

  function handlePasswordConfirmationChange(event: ChangeEvent<HTMLInputElement>) {
    setPasswordConfirmation(event.target.value);

    if (event.target.value !== password) {
      setError({ field: 'passwordConfirmation', message: 'Senhas divergentes' });
    } else {
      removeError('passwordConfirmation');
    }
  }

  const resetPassword = useCallback(
    async () => {
      await apiCall({
        apiToCall: authService.changePassword,
        isValidationToken: true,
        reqBody: JSON.stringify({
          senha: password,
          codUsu: userCode.current,
          perfilUsu: userProfile.current,
          codValidador: validationCode,
        }),
        onStartLoad: () => setIsLoading(true),
        onEndLoad: () => setIsLoading(false),
        actionAfterResponse: (response) => {
          if (response.codigo === 1) {
            toast.success('Senha alterada com sucesso. Realize o login!');
            navigate('/');
            return;
          }
          toast.error(`Erro. Por favor, tente novamente ${response.msg}`);
        },
        catchMessage: 'Erro. Por favor, tente novamente',
      });
    },
    [apiCall, navigate, password, validationCode],
  );

  const sendValidateCodeToEmail = useCallback(async () => {
    await apiCall({
      apiToCall: authService.sendValidateCode,
      queryParams: { cpf },
      isValidationToken: true,
      onStartLoad: () => setIsLoading(true),
      onEndLoad: () => setIsLoading(false),
      actionAfterResponse: (response) => {
        if (response.codigo === 0) {
          toast.error(`Não foi possível enviar o código em seu e-mail. Tente novamente (${response.msg})`);
          return;
        }
        toast.success('Use o código enviado em seu e-mail para alterar sua senha');
        setVisualizationKind('changePassword');
        userCode.current = response.codUsu;
        userProfile.current = response.perfilUsu;
      },
      catchMessage: 'Não foi possível enviar o código em seu e-mail. Tente novamente'
    });
  }, [apiCall, cpf]);

  return {
    isLoading,
    getErrorMessageByFieldName,
    cpf,
    handleCpfChange,
    resetPassword,
    isReadyToSendCode,
    visualizationKind,
    sendValidateCodeToEmail,
    handlePasswordChange,
    handlePasswordConfirmationChange,
    setValidationCode,
    isReadyToResetPassword,
    validationCode,
    password,
    passwordConfirmation,
  };
}
